<template>
  <div class="dizme_tm_section" id="apps">
    <div class="dizme_tm_apps">
      <div class="container">
        <div class="apps_wrapper">
          <!-- LEFT: the pitch, the requirement, and the two badges -->
          <div class="apps_copy">
            <div class="dizme_tm_main_title" data-align="left">
              <span>Mobile Apps</span>
              <h3>
                PBE Practice right on your device
              </h3>
            </div>

            <!-- The single most important thing on this section. It is stated
                 before the badges, not after them, so nobody taps through to a
                 store and then discovers they cannot sign in. -->
            <div class="apps_requirement" role="note">
              <span class="apps_requirement_icon" aria-hidden="true">🔒</span>
              <div>
                <p class="apps_requirement_title">
                  For MyPBE Pro members only
                </p>
                <p class="apps_requirement_body">
                  There's no sign-up in the app. Create your account and start
                  Pro on the web first.
                </p>
              </div>
            </div>

            <ol class="apps_steps">
              <li>
                <span class="apps_step_num">1</span>
                <div>
                  <h4>Create your free account</h4>
                  <p>
                    Sign up
                    <a href="https://app.mypbe.com/register">here</a>
                    in any browser.
                  </p>
                </div>
              </li>
              <li>
                <span class="apps_step_num">2</span>
                <div>
                  <h4>Start Pro on the web</h4>
                  <p>
                    Pro unlocks the app. Subscribe, or take a
                    <a href="#pricing">bulk subscription</a> seat from a coach.
                  </p>
                </div>
              </li>
              <li>
                <span class="apps_step_num">3</span>
                <div>
                  <h4>Download and sign in</h4>
                  <p>
                    Same email and password. Your chapters and progress come
                    with you.
                  </p>
                </div>
              </li>
            </ol>

            <div class="apps_badges">
              <a
                class="apps_badge"
                :href="iosUrl"
                target="_blank"
                rel="noopener"
              >
                <img
                  class="apps_badge_apple"
                  src="/img/badges/app-store-badge.svg"
                  alt="Download MyPBE on the App Store"
                />
              </a>
              <a
                class="apps_badge"
                :href="androidUrl"
                target="_blank"
                rel="noopener"
              >
                <img
                  class="apps_badge_google"
                  src="/img/badges/google-play-badge.png"
                  alt="Get MyPBE on Google Play"
                />
              </a>
            </div>
          </div>

          <!-- RIGHT: a drawn approximation of the app's own home screen, in the
               app's own colours. Markup rather than a screenshot so it stays
               crisp on any display and never goes stale by a pixel. -->
          <div class="apps_preview" aria-hidden="true">
            <div class="apps_phone">
              <div class="apps_phone_screen">
                <div class="apps_phone_notch"></div>

                <div class="apps_phone_topbar">
                  <img src="/img/logo/logo.png" alt="" class="apps_phone_logo" />
                  <div class="apps_phone_greeting">
                    <span class="apps_phone_hello">Welcome back,</span>
                    <span class="apps_phone_name">Pathfinder 👋</span>
                  </div>
                </div>

                <div class="apps_phone_hero">
                  <div class="apps_phone_stat">
                    <span class="apps_phone_stat_value">1,480</span>
                    <span class="apps_phone_stat_label">Activity points</span>
                  </div>
                  <div class="apps_phone_stat">
                    <span class="apps_phone_stat_value">86%</span>
                    <span class="apps_phone_stat_label">Readiness</span>
                    <span class="apps_phone_bar"
                      ><span style="width: 86%"></span
                    ></span>
                  </div>
                </div>

                <span class="apps_phone_section">Keep going</span>

                <div
                  v-for="row in previewRows"
                  :key="row.title"
                  class="apps_phone_row"
                >
                  <span
                    class="apps_phone_row_icon"
                    :style="{ background: row.gradient }"
                    >{{ row.emoji }}</span
                  >
                  <span class="apps_phone_row_text">
                    <span class="apps_phone_row_title">{{ row.title }}</span>
                    <span class="apps_phone_row_sub">{{ row.sub }}</span>
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div class="brush_1 wow fadeInLeft" data-wow-duration="1s">
        <img src="/img/brushes/service/5.png" alt="" />
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: "MobileAppComponent",
  data() {
    return {
      iosUrl: "https://apps.apple.com/us/app/mypbe/id6788212543",
      androidUrl:
        "https://play.google.com/store/apps/details?id=com.mypbe.app",
      // Mirrors the accent gradients the app itself gives these activities
      // (mobile/src/features.ts).
      previewRows: [
        {
          emoji: "⚡",
          title: "Lightning Challenge",
          sub: "60 seconds, as many as you can",
          gradient: "linear-gradient(135deg, #f7971e, #ffd200)",
        },
        {
          emoji: "📜",
          title: "Chapter Drill",
          sub: "Fill the blanks, chapter by chapter",
          gradient: "linear-gradient(135deg, #43e97b, #2bb673)",
        },
        {
          emoji: "🎓",
          title: "Assessment Zone",
          sub: "See how ready you really are",
          gradient: "linear-gradient(135deg, #f75023, #d73d1a)",
        },
      ],
    };
  },
  props: {
    dark: { type: Boolean },
  },
};
</script>

<style scoped>
/* The header is sticky, so a hash landing on this section (the "Mobile Apps"
   nav item, the Pro card's link, or a shared `mypbe.com/#apps`) would otherwise
   tuck the eyebrow underneath it. */
#apps {
  scroll-margin-top: 100px;
}

.dizme_tm_apps {
  position: relative;
  padding: 100px 0;
  overflow: hidden;
}

/* The global `.dizme_tm_main_title span` rule is written for the small orange
   eyebrow above a heading, so it pins *any* span in this block to 24px /
   weight 500 / inline-block with a bottom margin. A coloured word inside the
   h3 inherited all of that and rendered at half the heading's size, sitting off
   its baseline. Spans inside the h3 opt back out. */
.dizme_tm_apps .dizme_tm_main_title h3 span {
  font-size: inherit;
  font-weight: inherit;
  font-family: inherit;
  line-height: inherit;
  letter-spacing: inherit;
  display: inline;
  margin-bottom: 0;
}

.apps_wrapper {
  display: flex;
  align-items: center;
  gap: 60px;
}

.apps_copy {
  flex: 1 1 auto;
  min-width: 0;
}

.apps_preview {
  flex: 0 0 auto;
}

/* ---------------------------------------------------------------- the pitch */

.dizme_tm_apps .dizme_tm_main_title p {
  max-width: 560px;
}

/* The Pro-only requirement. Amber rather than green: this is a condition to
   read before tapping a badge, not a feature to celebrate. */
.apps_requirement {
  display: flex;
  gap: 14px;
  align-items: flex-start;
  margin: 28px 0 0;
  padding: 18px 20px;
  border-radius: 16px;
  border: 1px solid rgba(232, 169, 43, 0.6);
  background: rgba(240, 196, 92, 0.22);
  max-width: 560px;
}

.apps_requirement_icon {
  font-size: 20px;
  line-height: 1.3;
}

.apps_requirement_title {
  margin: 0 0 4px;
  font-family: "Jost", sans-serif;
  font-weight: 700;
  font-size: 17px;
  color: #1a1626;
}

.apps_requirement_body {
  margin: 0;
  font-size: 15px;
  line-height: 1.6;
}

body.dark .apps_requirement_title {
  color: #fff;
}

/* ---------------------------------------------------------------- the steps */

.apps_steps {
  list-style: none;
  margin: 28px 0 0;
  padding: 0;
  max-width: 560px;
}

.apps_steps li {
  display: flex;
  gap: 16px;
  align-items: flex-start;
  padding: 0 0 18px;
  position: relative;
}

/* The rail joining the three numbers, so they read as a sequence. */
.apps_steps li:not(:last-child)::before {
  content: "";
  position: absolute;
  left: 17px;
  top: 38px;
  bottom: 4px;
  width: 2px;
  border-radius: 2px;
  background: linear-gradient(
    180deg,
    rgba(247, 80, 35, 0.35),
    rgba(247, 80, 35, 0.08)
  );
}

.apps_step_num {
  flex: 0 0 auto;
  width: 36px;
  height: 36px;
  border-radius: 50%;
  display: grid;
  place-items: center;
  font-family: "Jost", sans-serif;
  font-weight: 700;
  font-size: 16px;
  color: #fff;
  background: linear-gradient(135deg, #f75023, #d73d1a);
  box-shadow: 0 6px 16px rgba(247, 80, 35, 0.3);
}

.apps_steps h4 {
  margin: 6px 0 4px;
  font-family: "Jost", sans-serif;
  font-size: 17px;
  font-weight: 600;
  line-height: 1.2;
}

.apps_steps p {
  margin: 0;
  font-size: 15px;
  line-height: 1.6;
}

.apps_steps a,
.apps_requirement a,
.apps_fineprint a {
  color: #f75023;
  font-weight: 600;
}

/* -------------------------------------------------------------- the badges */

.apps_badges {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 14px;
  margin-top: 8px;
}

.apps_badge {
  display: inline-block;
  border-radius: 10px;
  transition: transform 0.2s ease, box-shadow 0.2s ease;
}

.apps_badge:hover {
  transform: translateY(-2px);
  box-shadow: 0 10px 24px rgba(0, 0, 0, 0.18);
}

.apps_badge img {
  display: block;
}

/* Apple ships its badge edge-to-edge; Google ships its own clear space baked
   into the file (564x168 of art inside a 646x250 canvas). Heights are set so
   the two lock-ups end up the same visual size — 65 x 168/250 = 43.7. */
.apps_badge_apple {
  height: 44px;
  width: auto;
}

.apps_badge_google {
  height: 65px;
  width: auto;
}

.apps_fineprint {
  margin: 18px 0 0;
  font-size: 14px;
  line-height: 1.6;
  opacity: 0.85;
}

/* --------------------------------------------------------- the phone mockup */

.apps_phone {
  width: 290px;
  padding: 12px;
  border-radius: 44px;
  background: linear-gradient(160deg, #2b2740, #17141f);
  box-shadow: 0 30px 60px rgba(23, 20, 31, 0.35),
    0 0 0 1px rgba(255, 255, 255, 0.06) inset;
}

.apps_phone_screen {
  position: relative;
  border-radius: 34px;
  background: #f7f4f1;
  padding: 26px 16px 18px;
  overflow: hidden;
}

.apps_phone_notch {
  position: absolute;
  top: 8px;
  left: 50%;
  transform: translateX(-50%);
  width: 82px;
  height: 6px;
  border-radius: 6px;
  background: rgba(26, 22, 38, 0.18);
}

.apps_phone_topbar {
  display: flex;
  align-items: center;
  gap: 10px;
  margin-bottom: 14px;
}

.apps_phone_logo {
  width: 34px;
  height: 34px;
  border-radius: 50%;
}

.apps_phone_greeting {
  display: flex;
  flex-direction: column;
  line-height: 1.2;
}

.apps_phone_hello {
  font-size: 11px;
  color: #6f6b80;
}

.apps_phone_name {
  font-family: "Jost", sans-serif;
  font-size: 15px;
  font-weight: 600;
  color: #1a1626;
}

/* The app's own hero sweep — indigo to purple (mobile/src/theme.ts). */
.apps_phone_hero {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 10px;
  padding: 16px 14px;
  border-radius: 20px;
  background: linear-gradient(135deg, #4f46e5, #8b45bd);
}

.apps_phone_stat {
  display: flex;
  flex-direction: column;
  gap: 4px;
  padding: 10px;
  border-radius: 14px;
  background: rgba(255, 255, 255, 0.14);
}

.apps_phone_stat_value {
  font-family: "Jost", sans-serif;
  font-size: 20px;
  font-weight: 600;
  color: #fff;
  line-height: 1;
}

.apps_phone_stat_label {
  font-size: 8px;
  font-weight: 700;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  white-space: nowrap;
  color: rgba(255, 255, 255, 0.9);
}

.apps_phone_bar {
  display: block;
  height: 4px;
  border-radius: 4px;
  background: rgba(255, 255, 255, 0.28);
  overflow: hidden;
}

.apps_phone_bar span {
  display: block;
  height: 100%;
  border-radius: 4px;
  background: #fff;
}

.apps_phone_section {
  display: block;
  margin: 16px 0 8px;
  font-family: "Jost", sans-serif;
  font-size: 10px;
  font-weight: 700;
  letter-spacing: 0.12em;
  text-transform: uppercase;
  color: #8b8598;
}

.apps_phone_row {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 10px;
  margin-bottom: 8px;
  border-radius: 14px;
  background: #fff;
  box-shadow: 0 2px 8px rgba(26, 22, 38, 0.06);
}

.apps_phone_row_icon {
  flex: 0 0 auto;
  width: 32px;
  height: 32px;
  border-radius: 10px;
  display: grid;
  place-items: center;
  font-size: 15px;
}

.apps_phone_row_text {
  display: flex;
  flex-direction: column;
  min-width: 0;
}

.apps_phone_row_title {
  font-family: "Jost", sans-serif;
  font-size: 13px;
  font-weight: 600;
  color: #1a1626;
  white-space: nowrap;
}

.apps_phone_row_sub {
  font-size: 10px;
  color: #6f6b80;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

/* ------------------------------------------------------------- decoration */

.dizme_tm_apps .brush_1 {
  position: absolute;
  left: -60px;
  bottom: -40px;
  opacity: 0.35;
  pointer-events: none;
  z-index: -1;
}

.dizme_tm_apps .brush_1 img {
  max-width: 260px;
}

/* ------------------------------------------------------------- responsive */

@media (max-width: 1040px) {
  .apps_wrapper {
    gap: 40px;
  }

  .apps_phone {
    width: 250px;
  }
}

@media (max-width: 880px) {
  .dizme_tm_apps {
    padding: 70px 0;
  }

  /* Copy first, mockup after: on a narrow screen the reader is already
     holding a phone, so a picture of one is the least urgent thing here — and
     stacking it on top pushed the Pro-only requirement below the fold. */
  .apps_wrapper {
    flex-direction: column;
    align-items: flex-start;
    gap: 36px;
  }

  .dizme_tm_apps .dizme_tm_main_title,
  .apps_requirement,
  .apps_steps,
  .apps_fineprint {
    max-width: 100%;
  }

  .apps_badges {
    justify-content: flex-start;
  }
}

@media (max-width: 768px) {
  .dizme_tm_apps .brush_1 {
    display: none;
  }

  .apps_phone {
    width: 230px;
    padding: 10px;
    border-radius: 36px;
  }

  .apps_phone_screen {
    border-radius: 28px;
    padding: 22px 12px 14px;
  }

  .apps_requirement {
    padding: 16px;
  }

  .apps_requirement_title {
    font-size: 16px;
  }

  .apps_requirement_body,
  .apps_steps p {
    font-size: 14px;
  }

  .apps_badge_apple {
    height: 40px;
  }

  .apps_badge_google {
    height: 59px;
  }
}

@media (max-width: 480px) {
  .apps_badges {
    gap: 8px;
  }

  .apps_badge_apple {
    height: 38px;
  }

  .apps_badge_google {
    height: 56px;
  }
}
</style>
